import { attachListeners } from './events';
import { collectDevice, domIntrusions, windowSize } from './device';
import { createTransport, Transport } from './transport';
import { InitOptions } from './types';
import { now, randomId, templatePath } from './util';

export type { InitOptions, CollectorEvent, Batch } from './types';

// Bump on every change to what the collector sends. The judge maps versions to the
// signals they carry (judge/features.mjs CAPABILITIES): never rename or remove a field the
// judge reads.
export const VERSION = '0.3.0';
const SID_KEY = 'agentcatch_sid';

let state: { sid: string; transport: Transport; detach: () => void } | null = null;

/** Session ID shared by every page load in this tab. */
export const getSessionId = (): string => {
  if (state) return state.sid;
  try {
    const existing = sessionStorage.getItem(SID_KEY);
    if (existing) return existing;
    const sid = randomId();
    sessionStorage.setItem(SID_KEY, sid);
    return sid;
  } catch {
    return randomId();
  }
};

/**
 * Starts collecting on this page and returns the session ID, which the page attaches
 * to its login or action request so the backend can ask for a verdict.
 */
export const init = (opts: InitOptions): string => {
  if (state) return state.sid;
  const sid = getSessionId();
  const transport = createTransport({
    endpoint: opts.endpoint,
    key: opts.key,
    sid,
    pid: randomId(),
    version: VERSION,
    flushIntervalMs: opts.flushIntervalMs ?? 2000,
    publicKey: opts.publicKey,
    keyId: opts.keyId,
  });

  const nav = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
  let referrerOrigin: string | null = null;
  try {
    referrerOrigin = document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    /* ignore */
  }
  transport.push({
    type: 'page', t: now(), path: routeOf(opts.route), referrerOrigin, nav: nav?.type,
    tSinceNavStart: Math.round(performance.now()),
  });

  const baseline = {
    html: document.documentElement.getAttributeNames(),
    body: document.body ? document.body.getAttributeNames() : [],
  };
  collectDevice()
    .then((d) => transport.push({ type: 'device', t: now(), ...d }))
    .catch(() => undefined);
  // A page loaded hidden reports a 0x0 window: measure again once it's first shown
  if (document.visibilityState !== 'visible') {
    const onShow = () => {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', onShow);
      setTimeout(() => transport.push({ type: 'window', t: now(), window: windowSize() }), 250);
    };
    document.addEventListener('visibilitychange', onShow);
  }
  // Extensions inject after load; look again a few seconds in
  setTimeout(() => transport.push({ type: 'dom', t: now(), ...domIntrusions(baseline) }), 3000);

  const detach = attachListeners((e) => transport.push(e));
  state = { sid, transport, detach };
  return sid;
};

/** The page's route template, else the current path with ID-like segments replaced. */
const routeOf = (route?: string): string => (route ? templatePath(route) : templatePath(location.pathname));

/**
 * Records a navigation in a single-page app, which doesn't reload the page. Pass the
 * route template (e.g. "/applications/:token"), never the raw path.
 */
export const page = (route?: string): void => {
  if (!state) return;
  state.transport.push({ type: 'page', t: now(), path: routeOf(route), nav: 'spa' });
};

/**
 * Links this session to the signed-in user by an opaque ID (the dashboard's agent_id),
 * never an email or name. Anything that looks like an email is ignored.
 */
export const identify = (agentId: string): void => {
  if (!state || typeof agentId !== 'string' || !agentId || agentId.includes('@')) return;
  state.transport.push({ type: 'identify', t: now(), account: agentId.slice(0, 64) });
  state.transport.flush();
};

/**
 * Records an app-level action (e.g. "expense_submitted") and sends the queue at once:
 * actions are decision checkpoints, and headless agents can close the browser before the
 * next interval flush without firing pagehide.
 */
export const track = (name: string): void => {
  if (!state) return;
  state.transport.push({ type: 'action', t: now(), name });
  state.transport.flush();
};

const SOURCE = /^[a-z0-9-]{1,32}$/;

/**
 * Attaches another tool's verdict to the session (e.g. a bot-detection library), the way
 * the SDK attaches vendor device IDs. Only short flat values: never page text or input.
 */
export const annotate = (source: string, result: Record<string, string | number | boolean | null>): void => {
  if (!state || !SOURCE.test(source)) return;
  const flat: Record<string, string | number | boolean | null> = {};
  for (const [k, v] of Object.entries(result ?? {}).slice(0, 20)) {
    if (v === null || typeof v === 'number' || typeof v === 'boolean') flat[k] = v;
    else if (typeof v === 'string') flat[k] = v.slice(0, 64);
  }
  state.transport.push({ type: 'annotate', t: now(), source, result: flat });
  state.transport.flush();
};

/**
 * Ends the session (call on sign-out). Events on this page still go to the old
 * session; the next page load starts a new one.
 */
export const endSession = (): void => {
  if (state) {
    state.transport.push({ type: 'end', t: now() });
    state.transport.flush(true);
  }
  try {
    sessionStorage.removeItem(SID_KEY);
  } catch {
    /* ignore */
  }
};

export const stop = (): void => {
  if (!state) return;
  state.detach();
  state.transport.stop();
  state = null;
};
